import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Delete as DeleteIcon,
  AdminPanelSettings as AdminIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import api from '../../utils/api';

const UserBranchList = ({ open, onClose, user }) => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, branch: null });
  const [roleDialog, setRoleDialog] = useState({ open: false, branch: null });

  const fetchUserBranches = useCallback(async () => {
    if (!user) return;
    try {
      const response = await api.get(`/user-branches/user/${user.id}/branches`);
      setBranches(response.data.branches);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load user branches');
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (open && user) {
      fetchUserBranches();
    }
  }, [open, user, fetchUserBranches]);

  const handleRemoveFromBranch = async (branchId) => {
    try {
      await api.delete(`/user-branches/${branchId}/users/${user.id}`);
      await fetchUserBranches();
      setConfirmDialog({ open: false, branch: null });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove user from branch');
    }
  };

  const handleUpdateRole = async (branchId, isBranchAdmin) => {
    try {
      await api.put(`/user-branches/${branchId}/users/${user.id}/role`, {
        isBranchAdmin
      });
      await fetchUserBranches();
      setRoleDialog({ open: false, branch: null });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update user role');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <StoreIcon sx={{ mr: 1 }} />
          <Typography>
            Branch Assignments - {user?.first_name} {user?.last_name}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Branch Name</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Assigned Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {branches.map((branch) => (
                  <TableRow key={branch.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {branch.name}
                        {branch.is_main_branch && (
                          <Chip
                            label="Main Branch"
                            size="small"
                            color="primary"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {branch.is_branch_admin ? 'Branch Admin' : 'Staff'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={branch.assignment_status}
                        color={branch.assignment_status === 'active' ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(branch.assigned_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Update Role">
                        <IconButton
                          size="small"
                          onClick={() => setRoleDialog({ open: true, branch })}
                        >
                          <AdminIcon />
                        </IconButton>
                      </Tooltip>
                      {!branch.is_main_branch && (
                        <Tooltip title="Remove from Branch">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setConfirmDialog({ open: true, branch })}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {branches.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="textSecondary">
                        User is not assigned to any branches
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Confirm Remove Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, branch: null })}
      >
        <DialogTitle>Remove from Branch</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove this user from {confirmDialog.branch?.name}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, branch: null })}>
            Cancel
          </Button>
          <Button
            onClick={() => handleRemoveFromBranch(confirmDialog.branch?.id)}
            color="error"
            variant="contained"
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Role Dialog */}
      <Dialog
        open={roleDialog.open}
        onClose={() => setRoleDialog({ open: false, branch: null })}
      >
        <DialogTitle>Update Branch Role</DialogTitle>
        <DialogContent>
          <Box pt={2}>
            <Typography gutterBottom>
              Branch: {roleDialog.branch?.name}
            </Typography>
            <Button
              variant={roleDialog.branch?.is_branch_admin ? 'contained' : 'outlined'}
              onClick={() => handleUpdateRole(roleDialog.branch?.id, false)}
              sx={{ mr: 1 }}
            >
              Staff
            </Button>
            <Button
              variant={roleDialog.branch?.is_branch_admin ? 'outlined' : 'contained'}
              onClick={() => handleUpdateRole(roleDialog.branch?.id, true)}
            >
              Branch Admin
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialog({ open: false, branch: null })}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default UserBranchList; 